import { Link, useParams, Navigate } from 'react-router-dom';
import { BoardroomMode, Brand, EditorLink } from '@aigovops/design-system';
import { findEditor } from '../data/editors';

/**
 * /editors/:id — single editor card.
 *
 * brand-guide §6.5.1: "Every `Reviewed by X` is a link/hover that opens
 * an editor card: full name, credentials (bar admission, regulatory
 * background), current/prior roles, LinkedIn, contributed publications."
 *
 * If the slug doesn't match a real editor, redirect to /editors rather
 * than show a fake-name 404 that could leak or embarrass.
 */
export function EditorDetail() {
  const { id } = useParams<{ id: string }>();
  const editor = id ? findEditor(id) : undefined;
  if (!editor) return <Navigate to="/editors" replace />;

  return (
    <BoardroomMode>
      <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100dvh' }}>
        <header style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div
            style={{
              maxWidth: '80rem',
              margin: '0 auto',
              padding: 'var(--space-4) var(--space-6)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
            }}
          >
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                textDecoration: 'none',
              }}
            >
              <Brand variant="glyph" />
              <Brand variant="wordmark" />
            </Link>
            <nav
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-4)',
                fontSize: 'var(--font-size-2xs)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <Link to="/editors" style={{ color: 'var(--text-muted)' }}>← Editorial masthead</Link>
            </nav>
          </div>
        </header>

        <main style={{ maxWidth: '48rem', margin: '0 auto', padding: 'var(--space-12) var(--space-6)' }}>
          <article
            style={{
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              padding: 'var(--space-8)',
              borderRadius: 'var(--radius-md, 6px)',
            }}
          >
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-2xl)',
                margin: 0,
                lineHeight: 'var(--line-height-tight)',
              }}
            >
              {editor.displayName}
            </h1>
            <p
              style={{
                marginTop: 'var(--space-2)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
              }}
            >
              {editor.credentials.join(' · ')}
            </p>

            <section style={{ marginTop: 'var(--space-6)' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-3xs)',
                  color: 'var(--text-dim)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  margin: 0,
                }}
              >
                Domains
              </h2>
              <div
                style={{
                  marginTop: 'var(--space-2)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-2)',
                }}
              >
                {editor.domains.map((d) => (
                  <span
                    key={d}
                    style={{
                      border: '1px solid var(--border)',
                      padding: '2px var(--space-2)',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-muted)',
                      borderRadius: 'var(--radius-sm, 3px)',
                    }}
                  >
                    {d}
                  </span>
                ))}
              </div>
            </section>

            <section style={{ marginTop: 'var(--space-6)' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-3xs)',
                  color: 'var(--text-dim)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  margin: 0,
                }}
              >
                Public profile
              </h2>
              <p style={{ marginTop: 'var(--space-2)' }}>
                <EditorLink editor={editor} />
              </p>
            </section>
          </article>
        </main>

        <footer
          style={{
            borderTop: '1px solid var(--border)',
            marginTop: 'var(--space-16)',
            padding: 'var(--space-8) var(--space-6)',
            background: 'var(--surface)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
          }}
        >
          <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
            © 2026 <Brand variant="wordmarkRoman" /> · gacpro.io · hello@gacpro.io
          </div>
        </footer>
      </div>
    </BoardroomMode>
  );
}
